<template>
  <div
    :class="$style.container"
    :style="styles"
  >
    <slot
      name="before"
      :down="handleNext"
      :up="handlePrev"
      :enter="handleEnter"
      :left="handleLeft"
      :right="handleRight"
      :selected-item="flatItems[pointer]"
    />
    <div
      v-if="loading"
      :class="$style.item"
    >
      <slot name="loading">
        <loading-item />
      </slot>
    </div>
    <div
      v-else-if="!flatItems.length"
      :class="$style.item"
    >
      <slot name="not-found">
        <empty-item />
      </slot>
    </div>
    <virtual-scroller
      v-else
      ref="scroller"
      tabindex="0"
      :class="$style.scroller"
      :items="flatItems"
      :min-item-size="itemSize"
      :key-field="keyField"
      :page-mode="pageMode"
      @scroll="handleScroll"
      @keydown.down.exact.prevent="handleNext"
      @keydown.up.exact.prevent="handlePrev"
      @keydown.enter.exact.prevent="handleEnter"
      @keydown.left.exact.prevent="handleLeft"
      @keydown.right.exact.prevent="handleRight"
      @item-mouseenter="handleSelect"
    >
      <template #before>
        <slot name="before-list" />
      </template>
      <template #default="{ item, index, previousItem, nextItem }">
        <slot
          v-if="!!item[isLabelField]"
          name="item-header"
          :item="item"
          :index="index"
          :deep="item[deepField]"
        >
          <header-item>
            <slot
              name="item-header-title"
              :item="item"
            >
              {{ item[captionField] }}
            </slot>
          </header-item>
        </slot>
        <slot
          v-else
          name="item"
          :item="item"
          :index="index"
          :highlight="index === pointer"
          :disabled="item.disabled"
          :deep="item[deepField]"
          :active="!!item.active"
          :available="item.available"
          :previous-item="previousItem"
          :previous-deep="previousItem[deepField]"
          :next-item="nextItem"
          :next-deep="nextItem[deepField]"
          :caption-field="captionField"
        >
          <base-item
            :disabled="item.disabled"
            :deep="item[deepField]"
            :active="!!item.active"
          >
            <slot
              name="item-title"
              :item="item"
            >
              <span
                :title="item[captionField]"
                v-html="item[`${captionField}_highlight`] || item[captionField]"
              />
            </slot>
          </base-item>
        </slot>
      </template>
      <template #after>
        <slot name="after-list" />
      </template>
    </virtual-scroller>
    <slot
      name="after"
      :down="handleNext"
      :up="handlePrev"
      :enter="handleEnter"
    />
  </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { computed, ref, toRefs, watch } from 'vue';
import BaseItem from '@/components/list-item/base-item.vue';
import EmptyItem from '@/components/list-item/empty-item.vue';
import HeaderItem from '@/components/list-item/header-item.vue';
import LoadingItem from '@/components/list-item/loading-item.vue';
import VirtualScroller from '@/shared/legacy/ui/virtual-scroller/virtual-scroller.vue';
import { makeHierarchyMap } from '@/shared/legacy/lib/hierarchy/hierarchy-map';
import { isLabelField, keyField, deepField } from '@/shared/legacy/ui/base-list/composables/constants';
import { useFlatList } from '@/shared/legacy/ui/base-list/composables/use-flat-list';
import { useListPointer } from '@/shared/legacy/ui/base-list/composables/use-list-pointer';

export default {
  name: 'BaseList',
  components: {
    HeaderItem,
    BaseItem,
    EmptyItem,
    LoadingItem,
    VirtualScroller
  },
  props: {
    loading: Boolean,
    pageMode: VirtualScroller.props.pageMode,
    items: {
      type: Array,
      required: true
    },
    itemSize: {
      type: [Number, String],
      default: 40
    },
    showInactive: Boolean,
    maxHeight: {
      type: [Number, String],
      default: null
    },
    hierarchyMap: {
      type: Object,
      default: () => makeHierarchyMap({})
    },
    loopPointer: {
      type: Boolean,
      default: true
    },
    autopoint: {
      type: Boolean,
      default: true
    },
    itemsPostprocessing: {
      type: Function,
      default: (items) => items
    },
    captionField: {
      type: String,
      default: 'name'
    }
  },
  emits: ['scroll', 'enter-item', 'left-key', 'right-key'],
  setup(props, { emit }) {
    const {
      showInactive,
      items,
      itemsPostprocessing,
      hierarchyMap,
      loopPointer,
      loading,
      maxHeight,
      autopoint
    } = toRefs(props);
    const scroller = ref(null);
    const disableSelection = ref(false);
    const { items: flatItems } = useFlatList(items, {
      showInactive,
      parentChildMap: computed(() => hierarchyMap.value?.parentChildMap || {}),
      postprocessing: itemsPostprocessing
    });
    const { next, prev, select, pointer, reset } = useListPointer(flatItems, {
      loop: loopPointer,
      autopoint
    });

    watch(loading, (flag) => {
      if (!flag) {
        reset();
      }
    });

    const styles = computed(() => {
      const normalizedMaxHeight
        = typeof maxHeight.value === 'number' ? `${maxHeight.value}px` : maxHeight.value;
      return {
        maxHeight: normalizedMaxHeight
      };
    });

    function scrollToIndex(index) {
      if (!scroller.value) {
        return;
      }
      disableSelection.value = true;
      scroller.value.scrollToIndex(index);
      setTimeout(() => {
        disableSelection.value = false;
      }, 100);
    }
    function handleNext() {
      next();
      scrollToIndex(pointer.value);
    }
    function handlePrev() {
      prev();
      scrollToIndex(pointer.value);
    }

    const highlightedItem = computed(() => {
      return flatItems.value[pointer.value];
    });

    return {
      styles,
      pointer,
      flatItems,
      keyField,
      isLabelField,
      deepField,
      highlightedItem,
      scrollToIndex,
      handleSelect: debounce((index) => {
        if (!disableSelection.value) {
          select(index);
        }
      }, 30),
      handleScroll: () => {
        emit('scroll');
      },
      handleNext,
      handlePrev,
      handleEnter: () => {
        if (flatItems.value[pointer.value].available === false) {
          return;
        }
        emit('enter-item', flatItems.value[pointer.value]);
      },
      handleLeft: () => {
        emit('left-key', highlightedItem.value);
      },
      handleRight: () => {
        emit('right-key', highlightedItem.value);
      },
      scroller
    };
  }
};
</script>

<style module>
.container {
  height: 100%;
  display: flex;
  flex-direction: column;
}
.scroller {
  outline: none;
  flex-grow: 1;
  text-align: left;
}
</style>

<i18n lang="json">{}</i18n>
